import React, { useCallback, useEffect, useState } from 'react';
import moment, { Moment } from 'moment';
import { editWorkOrder } from 'shared/api/workOrders';
import WorkOrder from 'shared/interfaces/WorkOrder.interface';
import { useDispatch, useSelector } from 'react-redux';
import { updateOneWorkOrder } from 'shared/store/features/workOrdersSlice';
import { setAlert } from 'shared/store/features/alertSlice';
import { toggleEditModalView } from 'shared/store/features/editWorkOrderModalSlice';
import { setSelectedWorkOrders } from 'shared/store/features/workOrdersTableSlice';
import { RootState } from 'shared/store/rootReducer';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';

const useEditWorkOrderModalLogic = () => {
  const selectedWorkOrder = useSelector(
    (state: RootState) => state.workOrdersTable.selectedWorkOrders[0]
  );
  const [editedWorkOrder, setEditedWorkOrder] = useState<WorkOrder>(selectedWorkOrder);
  const [noImpressionDataNeeded, setNoImpressionDataNeeded] = useState(!selectedWorkOrder.isImpressionDataNeeded);
  const [activeSaveButton, setActiveSaveButton] = useState(false);
  const [endDateValue, setEndDateValue] = useState<Moment>(moment(selectedWorkOrder.endDate));
  const [impressions, setImpressions] = useState<number | null>(selectedWorkOrder.impressions);
  const [noImpressionDataReason, setNoImpressionDataReason] = useState(selectedWorkOrder.noImpressionDataReason);
  const [isLoading, setIsLoading] = useState(false);
  const { isEditWorkOrderModalOpened } = useSelector(
    (state: RootState) => state.editWorkOrderModal
  );
  const dispatch = useDispatch();

  const activeSaveButtonHandler = useCallback(() => {
    setActiveSaveButton(Boolean(endDateValue && (impressions || noImpressionDataReason)));
  }, [endDateValue, impressions, noImpressionDataReason]);

  useEffect(() => {
    setEndDateValue(moment(selectedWorkOrder.endDate));
    setImpressions(selectedWorkOrder.impressions);
    setNoImpressionDataReason(selectedWorkOrder.noImpressionDataReason);
    setNoImpressionDataNeeded(!selectedWorkOrder.isImpressionDataNeeded);
  }, [selectedWorkOrder, isEditWorkOrderModalOpened]);

  useEffect(() => {
    activeSaveButtonHandler();
  }, [activeSaveButtonHandler]);

  const updateWorkOrder = useCallback(() => {
    setEditedWorkOrder({
      ...selectedWorkOrder,
      endDate: endDateValue,
      impressions,
      isImpressionDataNeeded: noImpressionDataNeeded,
      noImpressionDataReason
    });
  }, [noImpressionDataNeeded, impressions, endDateValue, noImpressionDataReason, selectedWorkOrder]);

  useEffect(() => {
    updateWorkOrder();
  }, [updateWorkOrder]);

  const onValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    switch (event.target.name) {
      case 'impressions':
        return setImpressions(parseInt(event.target.value, 10));
      case 'noImpressionsDataReason':
        return setNoImpressionDataReason(event.target.value);
      default:
        return '';
    }
  };

  const onDateChangeHandler = useCallback((date: MaterialUiPickersDate) => {
    if (date) {
      date.set({
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0
      });
      setEndDateValue(date.add(date.utcOffset(), 'minutes'));
    }
  }, [setEndDateValue]);

  const onDialogClose = useCallback(() => {
    dispatch(toggleEditModalView({ isEditWorkOrderModalOpened: false }));
  }, [dispatch]);

  const impressionsDataNeededHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNoImpressionDataNeeded(event.target.checked);
    if (event.target.checked) {
      setImpressions(null);
    } else {
      setNoImpressionDataReason('');
    }
  };

  const onEditionSubmit = () => {
    const workOrderDTO = {
      impressions,
      noImpressionDataReason,
      isImpressionDataNeeded: !noImpressionDataNeeded,
      endDate: endDateValue.format('YYYY-MM-DDTHH:MM:SS')
    };
    if (editedWorkOrder.pricingModel !== 'CPM') {
      delete workOrderDTO.impressions;
      delete workOrderDTO.noImpressionDataReason;
      delete workOrderDTO.isImpressionDataNeeded;
    }
    if (!workOrderDTO.isImpressionDataNeeded) {
      workOrderDTO.impressions = null;
    }
    setIsLoading(true);
    editWorkOrder(
      editedWorkOrder.id,
      workOrderDTO
    )
      .then((changedWorkOrder: WorkOrder) => {
        onDialogClose();
        dispatch(updateOneWorkOrder({ changedWorkOrder }));
        dispatch(
          setAlert({
            open: true,
            variant: 'success',
            text: 'Work order was edited'
          })
        );
      })
      .catch(error => {
        dispatch(
          setAlert({
            open: true,
            variant: 'danger',
            text: error.message
          })
        );
      })
      .finally(() => {
        setIsLoading(false);
        dispatch(setSelectedWorkOrders({ selectedWorkOrders: [] }));
      });
  };

  return {
    noImpressionDataNeeded,
    activeSaveButton,
    impressions,
    endDateValue,
    noImpressionDataReason,
    onEditionSubmit,
    onValueChange,
    impressionsDataNeededHandler,
    setIsImpressionDataNeeded: setNoImpressionDataNeeded,
    setImpressions,
    setEndDateValue,
    setNoImpressionDataReason,
    editedWorkOrder,
    activeSaveButtonHandler,
    isDialogOpened: isEditWorkOrderModalOpened,
    onDialogClose,
    isLoading,
    onDateChangeHandler
  };
};

export default useEditWorkOrderModalLogic;
